<template>
    <BasePanel 
        headline="lang_ACCOUNTS_MAPPINGS_CAPTION_COLUMN_MAPPINGS_PANEL"
        :alvqa="alvqa"
    >
        <template v-slot:panelContent>
            <v-data-table
                :headers="headers"
                :items="listItems"
                hide-default-footer
                :loading="isLoading"
                :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
                :items-per-page="itemsPerPage"
            >

            <!-- Topslot -->
            <template v-slot:top>
                <v-row class="pb-3">
                    <v-spacer></v-spacer>
                    <v-col cols="3">
                        <BaseButton 
                            @click="addNewRow"
                            label="lang_GENERAL_BUTTON_ADD_ROW"
                            block
                            :alvqa="alvqa + '-button-add-row'"
                            :disabled="isDisabled"
                        />
                    </v-col>
                </v-row>
            </template>

            <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

            <!-- Items -->
            <template v-slot:[`item.columnNumber`]="{ item }">
                <div :alvqa="alvqa + '-list-row-' + item.id + '-text-column-number'">
                    {{ item.templateColumn }}
                </div>
            </template>

            <template v-slot:[`item.entityName`]="{ item }">
                <div :alvqa="`${alvqa}-list-row-${item.id}-text-item-name`">
                    {{ $t(getItemName(item.entityName)) }}
                </div>
            </template>

            <template v-slot:[`item.alvField`]="{ item }">
                <div :alvqa="alvqa + '-list-row-' + item.id + '-text-alv-field'">
                    {{ $t(getAlvFieldName(item)) }}         
                </div>
            </template>

            <template v-slot:[`item.formula`]="{ item }">
                <BaseTooltip
                    :alvqa="alvqa + '-list-row-' + item.id + '-text-formula'"
                >
                    <template v-slot:content>
                        <div class="ellipsis">
                            {{ item.formula }}
                        </div>
                    </template>
                    <template v-slot:text>
                        {{ item.formula }}
                    </template>
                </BaseTooltip>
            </template>

            <!-- Buttons -->
            <template v-slot:[`item.delete`]="{ item, index }">
                <BaseIcon 
                    @click="deleteRow(item)"
                    icon="delete"
                    color="iconDelete"
                    tooltipText="lang_GENERIC_CAPTION_DELETE"
                    :alvqa="alvqa + '-list-row-' + index + '-icon-delete'"
                />
                </template>
                <template v-slot:[`item.inlineEdit`]="{ item, index }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-' + index + '-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
            </v-data-table>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseTooltip from '@/components/_base/BaseTooltip'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseButton from '@/components/_buttons/BaseButton'

import 
    { 
        DropdownAccountMappingsAlvFields,
        DropdownAccountMappingsDomainsOptions
    } 
from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "ColumnMappingsEditPanel",
    components: {
        BasePanel,
        BaseTooltip,
        BaseIcon,
        BaseButton,
    },
    data() {
        return {
            expandedItems: [],
            alvqa: "account-mappings-edit-form-panel-column-mappings"
        }
    }, 
    computed: {

        listItems() { 
            this.$store.commit('setIsLoading', true)
            this.$store.dispatch('fetchColumnMappings')
            this.$store.commit('setIsLoading', false)
            return this.$store.getters.getColumnMappings 
        },

        isLoading() { return this.$store.getters.getIsLoading },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },

        headers() {
            return [
                {
                    text: this.$t('lang_ACCOUNTS_MAPPINGS_CAPTION_TEMPLATE_COLUMN'), 
                    value: "columnNumber", 
                    align: "left", 
                    class: 'text-no-wrap',
                    width: '220px',
                    sortable: false,
                    alvqa: this.alvqa + "-header-template-column",
                },
                {
                    text: this.$t('lang_ACCOUNT_MAPPINGS_CAPTION_ITEM'),
                    value: 'entityName',
                    align: 'left',
                    class: 'text-no-wrap',
                    width: '220px',
                    sortable: true,
                    alvqa: this.alvqa + '-header-item-name'
                },
                {
                    text: this.$t('lang_ACCOUNTS_MAPPINGS_CAPTION_ALV_FIELD'), 
                    value: "alvField", 
                    align: "left", 
                    class: 'text-no-wrap',
                    width: '200px',
                    sortable: false,
                    alvqa: this.alvqa + "-header-alv-fields"
                },
                {
                    text: this.$t('lang_ACCOUNTS_MAPPINGS_CAPTION_FORMULA'), 
                    value: "formula", 
                    align: "left", 
                    class: 'text-no-wrap',
                    sortable: true,
                    alvqa: this.alvqa + "-header-formula"
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },
        isDisabled() {
            // as long as no file has been uploaded, there are no headers = no "add row"
            let fileName = this.$store.getters.getMappingsDetailsFileName
            let direction = this.$store.getters.getMappingsDetailsDirection
            if (fileName == null || fileName == '' || direction == 'export')
                return true
            else
                return false
        },

    },

    methods: {
        editInlineFunction(item) {
            this.$store.commit('setSelectedColumnMappings', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))
            // set the copy in dialog details to edit
            this.$store.commit('setColumnMappingsEditingDialogDetails', detailsDeepCopy)
            // this.$store.commit('setTracksArtistsArtistOptions', [item.artist])
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setShowColumnMappingsEditingDialog', true) 
        },

        getAlvFieldName(item) {
            let allAlvFields    = DropdownAccountMappingsAlvFields
            let domain          = item.entityName ? item.entityName : this.$store.getters.getMappingsDetailsDomain
            let direction       = this.$store.getters.getMappingsDetailsDirection  
            let alvFields       = allAlvFields[direction + "_" + domain]
            if(alvFields !== undefined) {
                let res = alvFields.find(field => {
                    return field.value === item.alvField
                })
                return res ? res.text : ''
            }
        },

        getItemName(itemName) {
            let rootDomain = this.$store.getters.getMappingsDetailsDomain

            let option_map = {
                'publishing_works': [
                    {
                        value: 'publishing_works',
                        text: 'lang_PUBLISHING_CAPTION_WORKS'
                    },
                    {
                        value: 'publishing_works_interested_parties',
                        text: 'lang_PUBLISHING_WORKS_CAPTION_INTERESTED_PARTIES'
                    },
                    {
                        value: 'publishing_works_work_ids',
                        text: 'lang_GENERIC_CAPTION_OTHER_IDS'
                    },
                    {
                        value: 'publishing_works_instrumentations',
                        text: 'lang_PUBLISHING_WORKS_CAPTION_INSRUMENTATION'
                    },
                    {
                        value: 'publishing_works_other_titles',
                        text: 'lang_PUBLISHING_WORKS_CAPTION_OTHER_TITLES'
                    },
                    {
                        value: 'publishing_works_contracts',
                        text: 'lang_GENERIC_CAPTION_CONTRACTS'
                    }
                ],
                'publishing_sheets': [
                    {
                        value: 'publishing_sheets',
                        text: 'lang_PUBLISHING_CAPTION_SHEETS'
                    },
                    {
                        value: 'publishing_sheets_works',
                        text: 'lang_PUBLISHING_CAPTION_WORKS'
                    }
                ],
                'publishing_projects': [
                    {
                        value: 'publishing_projects',
                        text: 'lang_PUBLISHING_CAPTION_PROJECTS'
                    },
                    {
                        value: 'publishing_sheets',
                        text: 'lang_PUBLISHING_CAPTION_SHEETS'
                    },
                    {
                        value: 'publishing_sheets_works',
                        text: 'lang_PUBLISHING_CAPTION_WORKS'
                    }
                ],
                'label_releases': [
                    {
                        value: 'label_releases',
                        text: 'lang_LABEL_RELEASES'
                    },
                    {
                        value: 'label_tracks',
                        text: 'lang_LABEL_TRACKS'
                    },
                    {
                        value: 'label_releases_other_ids',
                        text: 'lang_GENERIC_CAPTION_OTHER_IDS'
                    },
                    {
                        value: 'label_tracks_other_ids',
                        text: 'lang_GENERIC_CAPTION_OTHER_IDS'
                    }
                ],
                'label_tracks': [
                    {
                        value: 'label_tracks',
                        text: 'lang_LABEL_TRACKS'
                    },
                    {
                        value: 'label_tracks_other_ids',
                        text: 'lang_GENERIC_CAPTION_OTHER_IDS'
                    }
                ],
                'az_persons': [
                    {
                        value: 'az_persons',
                        text: 'lang_GENERIC_CAPTION_DOMAIN_AZ_PERSONS'
                    },
                    {
                        value: 'az_persons_metadata',
                        text: 'lang_GENERIC_AZ_PANEL_CAPTION_METADATA'
                    },
                    {
                        value: 'az_persons_finance',
                        text: 'lang_AZ_PERSONS_PANEL_CAPTION_FINANCE'
                    },
                    {
                        value: 'az_persons_other_ids',
                        text: 'lang_AZ_PERSONS_CAPTION_PANEL_OTHER_IDS'
                    },
                ]
            }
            if (option_map[rootDomain]) {                
                let foundItem = option_map[rootDomain].find((v) => v.value === itemName)

                return foundItem ? foundItem.text : ''
            } 

            let options = DropdownAccountMappingsDomainsOptions
            // find root domain from dropdown options and return it as single item array
            return options.find((v) => v.direction === 'import' && rootDomain === v.value).text
        },

        addNewRow(){
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))
            this.$store.commit('setShowColumnMappingsEditingDialog', true)
            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultColumnMappings)) 
            this.$store.commit('setColumnMappingsEditingDialogDetails', defaultValues)
        },

        deleteRow(item) {
            this.$store.commit("setSelectedColumnMappings", item)
            this.$store.commit('setDeletionDialogContext', 'SelectedColumnMappings')
            this.$store.commit('setShowDeletionDialog', true)
        },


    }
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }
</style>